import React from 'react'
import { useLocation } from 'react-router-dom'
import queryString from 'query-string'

import TaskRow, {
	TaskListHeader,
	TaskRowSkeleton,
} from 'components/cards/TaskRow/TaskRow'

import themeStyles from '../../assets/styles/theme.module.sass'
import { useFetchList } from 'store'

import styles from './TasksController.module.sass'
import NoData from 'components/general/NoData/NoData'
import LazyLoader from 'components/general/LazyLoader/LazyLoader'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import Widget from '@jimmyboys/liita-ui/dist/components/Widget'
import Collection from '@jimmyboys/liita-ui/dist/components/Collection'
import { Task, Options } from 'models'
import TasksFilters from './TasksFilters'

const DEFAULT_PAGE_SIZE = 48
const { rowHeight, rowMargin } = themeStyles

const TasksController = () => {
	const location = useLocation()
	const { i18n, screenSize } = useGlobal()

	const rowLabels = {
		options: i18n`Options`,
		cancel: i18n`Cancel`,
		seeProcess: i18n`See Process`,
		'very-low': i18n`Very Low`,
		low: i18n`Low`,
		medium: i18n`Medium`,
		high: i18n`High`,
		'very-high': i18n`Very High`,
	}

	const taskTypesUrl = `/metadata/task_types`
	const [queryParams, , setSearchParams] = useQueryParams(location)
	const pageList = new Array(parseInt(queryParams.page, 10) || 1)
	const pagesRequests = pageList.fill(null).map((_, index) => {
		const pageQueryParams = {
			...queryParams,
			page: index,
			pageSize: DEFAULT_PAGE_SIZE,
		}
		const qs = queryString.stringify(pageQueryParams)
		const pageUrl = `/tasks?${qs || ''}`
		return pageUrl
	})

	const pagesMappers = pageList.fill(Task.fromJSON)

	const {
		contents: [taskTypes, ...pages],
	} = useFetchList(
		[taskTypesUrl, ...pagesRequests],
		[Options.fromSimpleObjectJSON, ...pagesMappers],
		undefined,
		true
	)

	const isLoading = pages.some(({ state }) => state === 'loading')

	const lastPageContents = pages[pages.length - 1]?.contents

	const hasMore =
		Array.isArray(lastPageContents) &&
		lastPageContents.length === DEFAULT_PAGE_SIZE

	const items = pages
		.map(({ state, contents }) => {
			const isPageLoading = state === 'loading'
			return isPageLoading
				? new Array(DEFAULT_PAGE_SIZE).fill({
						$cardName: 'TaskRowSkeleton',
						$layout: screenSize,
				  })
				: Array.isArray(contents)
				? contents.map((data) => ({
						...data,
						$cardName: 'TaskRow',
						$layout: screenSize,
						rowLabels,
				  }))
				: []
		})
		.flat()

	return (
		<>
			<Widget paddings={['small', 'normal', '']} applyMaxWidth>
				<TasksFilters
					taskTypesFilters={
						taskTypes?.contents && taskTypes?.contents.length
							? taskTypes.contents
							: []
					}
				/>
			</Widget>
			<Widget
				className={styles['list-header']}
				paddings={['normal', 'normal', '']}
				applyMaxWidth
			>
				<TaskListHeader $layout={screenSize} />
			</Widget>
			<Widget paddings={['small', 'normal', '']} applyMaxWidth>
				{!isLoading && !items.length ? (
					<NoData
						applyMaxWidth
						paddings={['normal', 'normal']}
						message={i18n`Current filters do not match any process.\n\nTry with different filters or reset them.`}
					/>
				) : null}
				<Collection
					className={styles['processes-list']}
					items={items}
					itemsHeight={parseFloat(rowHeight) + parseFloat(rowMargin)}
					renderAhead={20}
					cards={{
						TaskRowSkeleton,
						TaskRow,
					}}
				/>
			</Widget>
			<Widget paddings={['', 'normal', 'normal']} applyMaxWidth>
				<LazyLoader
					isLoading={isLoading}
					hasMore={hasMore}
					onVisible={() => {
						const nextPage = parseInt(queryParams.page, 10) || 1
						setSearchParams({ page: nextPage + 1 })
					}}
				/>
			</Widget>
		</>
	)
}

export default TasksController
